import { useState, useCallback, useRef, useMemo, FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import PhoneInput, { type PhoneInputHandle } from '@/components/PhoneInput'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  type TitleOption,
  genderedTitleValue,
  titleSelectValue,
  visibleTitleOptions,
} from '@/lib/registrationFormShared'
import {
  Search,
  Loader2,
  MoreHorizontal,
  Printer,
  FileDown,
  Upload,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
  ExternalLink,
  Pencil,
  Download,
  Check,
  Clock,
  X,
  Trash2,
  MessageSquare,
  Eye,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CustomFieldDef {
  name: string
  nameEn: string
  fieldType: string
  required: boolean
  order: number
  options: string[]
  optionIconUrls?: Array<string | null>
}

interface EventUserData {
  id: number
  token: string
  createdAt: string
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  email: string | null
  organization: string | null
  position: string | null
  country: string | null
  city: string | null
  userType: string | null
  printed: boolean
  checkedIn: boolean
  rsvp: string | null
  approved: boolean | null
  title: string | null
  titleLabel: string | null
  gender: string | null
  age: number | null
  idNumber: string | null
  passportNumber: string | null
  bloodType: string | null
  tableNumber: number | null
  seatRow: string | null
  seatColumn: number | null
  imageUrl: string | null
  registrationGroupToken: string | null
  primaryRegistration: boolean
  bundleSize: number
  customFields: Record<string, string>
  notesCount: number
}

interface FormFieldData {
  field: string
  order: number
  custom: boolean
}

interface Props {
  locale: string
  logoUrl: string | null
  accountName: string
  userEmail: string
  canEditAccount: boolean
  wideLayout?: boolean
  urls: {
    events: string
    accountSettings: string
    changePassword: string
    logout: string
    eventDashboard: string
    eventUsersJson: string
    newAdmin: string
    importNew: string
    bulkCreateNew: string
    renderAll: string
    createAdmin: string
    badgeBase: string
    updateAdminBase: string
    rsvpBase: string
    resendRegistrationBase: string
    approve: string
    toggleCheckIn: string
    exportCsvBase: string
    deleteBase: string
    detailsBase: string
  }
  event: { id: number; name: string }
  eventUsers: EventUserData[]
  pagination: {
    currentPage: number
    perPage: number
    totalCount: number
    totalPages: number
  }
  registrationFields: string[]
  formFields: FormFieldData[]
  customFieldDefs: CustomFieldDef[]
  eventUserTypes: string[]
  titleFieldType?: 'text' | 'select'
  titleOptions?: TitleOption[]
  positionFieldType?: 'text' | 'select'
  positionOptions?: string[]
  filters: {
    search: string
    sort: string
    direction: string
    userType: string
    printed: string
    checkedIn: string
    rsvp: string
  }
  physicalBadgePrint?: boolean
  physicalBadgeWidth?: string | null
  physicalBadgeHeight?: string | null
  permissions: {
    canCreate: boolean
    canEdit: boolean
    canDestroy: boolean
  }
  translations: Record<string, string>
}

/* ------------------------------------------------------------------ */
/*  Field key to EventUser property mapping                            */
/* ------------------------------------------------------------------ */

const FIELD_MAP: Record<string, keyof EventUserData> = {
  first_name: 'firstName',
  last_name: 'lastName',
  phone_number: 'phoneNumber',
  email: 'email',
  organization: 'organization',
  position: 'position',
  country: 'country',
  city: 'city',
  title: 'titleLabel',
  gender: 'gender',
  age: 'age',
  id_number: 'idNumber',
  passport_number: 'passportNumber',
  blood_type: 'bloodType',
  table_number: 'tableNumber',
  seat_row: 'seatRow',
  seat_column: 'seatColumn',
  user_type: 'userType',
}

const INPUT_CLS = 'h-8 rounded-md border border-[#E8E8E5] bg-white px-3 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] focus-visible:outline-none w-full'

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EventUsersIndex({
  locale,
  logoUrl,
  accountName,
  userEmail,
  canEditAccount,
  wideLayout,
  urls,
  event,
  eventUsers,
  pagination,
  registrationFields,
  formFields,
  customFieldDefs,
  eventUserTypes,
  titleFieldType = 'text',
  titleOptions = [],
  positionFieldType = 'text',
  positionOptions = [],
  filters,
  physicalBadgePrint = false,
  physicalBadgeWidth,
  physicalBadgeHeight,
  permissions,
  translations: t,
}: Props) {
  const [searchValue, setSearchValue] = useState(filters.search)
  const [searchVisible, setSearchVisible] = useState(filters.search.length > 0)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [editUser, setEditUser] = useState<EventUserData | null>(null)
  const [editForm, setEditForm] = useState<Record<string, string>>({})
  const [editCustomForm, setEditCustomForm] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<Record<string, string>>({})
  const [createCustomForm, setCreateCustomForm] = useState<Record<string, string>>({})
  const [creating, setCreating] = useState(false)
  const [createPhoneError, setCreatePhoneError] = useState('')
  const [editPhoneError, setEditPhoneError] = useState('')
  const createPhoneRef = useRef<PhoneInputHandle>(null)
  const editPhoneRef = useRef<PhoneInputHandle>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasPhoneField = formFields.some((ff) => ff.field === 'Phone number')
  const hasUserTypes = eventUserTypes.length > 0

  const visibleTitles = useMemo(
    () => visibleTitleOptions(titleOptions, locale),
    [titleOptions, locale],
  )

  const setCreateField = (key: string, value: string) =>
    setCreateForm((current) => {
      if (key === 'gender') {
        return { ...current, gender: value, title: genderedTitleValue(visibleTitles, current.title || '', value) }
      }
      if (key === 'title') {
        return { ...current, title: genderedTitleValue(visibleTitles, value, current.gender || '') }
      }
      return { ...current, [key]: value }
    })

  const setEditField = (key: string, value: string) =>
    setEditForm((current) => {
      if (key === 'gender') {
        return { ...current, gender: value, title: genderedTitleValue(visibleTitles, current.title || '', value) }
      }
      if (key === 'title') {
        return { ...current, title: genderedTitleValue(visibleTitles, value, current.gender || '') }
      }
      return { ...current, [key]: value }
    })

  const renderTitleField = (
    form: Record<string, string>,
    setField: (key: string, value: string) => void,
  ) => {
    if (titleFieldType === 'select' && titleOptions.length > 0) {
      return (
        <>
          <select
            className={INPUT_CLS}
            value={titleSelectValue(visibleTitles, form.title || '')}
            onChange={(e) => setField('title', e.target.value)}
          >
            <option value="">{t.pleaseSelect}</option>
            {visibleTitles.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {form.title === 'other' && (
            <Input
              className={`${INPUT_CLS} mt-2`}
              value={form.other_title || ''}
              onChange={(e) => setField('other_title', e.target.value)}
              placeholder={t.otherTitle || t.pleaseSpecifyTitle || 'Please specify title'}
            />
          )}
        </>
      )
    }

    return (
      <Input
        className={INPUT_CLS}
        value={form.title || ''}
        onChange={(e) => setField('title', e.target.value)}
      />
    )
  }

  const renderPositionField = (
    form: Record<string, string>,
    setField: (key: string, value: string) => void,
  ) => {
    if (positionFieldType === 'select' && positionOptions.length > 0) {
      return (
        <select
          className={INPUT_CLS}
          value={form.position || ''}
          onChange={(e) => setField('position', e.target.value)}
        >
          <option value="">{t.pleaseSelect}</option>
          {positionOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      )
    }

    return (
      <Input
        className={INPUT_CLS}
        value={form.position || ''}
        onChange={(e) => setField('position', e.target.value)}
      />
    )
  }

  /* ---------- Navigation helper (server-side) ---------- */

  const navigate = useCallback(
    (overrides: Record<string, string | number>) => {
      const params: Record<string, string | number> = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: filters.search,
        sort: filters.sort,
        direction: filters.direction,
        user_type: filters.userType,
        printed: filters.printed,
        checked_in: filters.checkedIn,
        rsvp: filters.rsvp,
        ...overrides,
      }
      // Remove empty params
      Object.keys(params).forEach((k) => {
        if (params[k] === '' || params[k] === undefined) delete params[k]
      })
      router.get(window.location.pathname, params, {
        preserveState: true,
        preserveScroll: true,
        only: ['eventUsers', 'pagination', 'filters'],
      })
    },
    [pagination, filters],
  )

  const refreshEventUsers = useCallback(() => {
    router.get(window.location.pathname, {
      page: pagination.currentPage,
      per_page: pagination.perPage,
      search: filters.search,
      sort: filters.sort,
      direction: filters.direction,
      user_type: filters.userType,
      printed: filters.printed,
      checked_in: filters.checkedIn,
      rsvp: filters.rsvp,
    }, {
      preserveScroll: true,
      preserveState: true,
      only: ['eventUsers', 'pagination', 'filters'],
    })
  }, [filters, pagination])

  const printHtmlInFrame = useCallback(
    (html: string, title: string, delayMs: number, extraHead = '') => {
      return new Promise<void>((resolve) => {
        const frame = document.createElement('iframe')
        frame.style.cssText = 'position:absolute;top:-10000px;width:0;height:0;border:none'
        document.body.appendChild(frame)

        const cleanup = () => {
          if (frame.parentNode) document.body.removeChild(frame)
          refreshEventUsers()
          resolve()
        }

        const doc = frame.contentDocument || frame.contentWindow?.document
        if (!doc) {
          cleanup()
          return
        }

        const looksLikeDocument = /<html[\s>]/i.test(html)
        doc.open()
        if (looksLikeDocument) {
          const injected = extraHead ? html.replace(/<\/head>/i, `${extraHead}</head>`) : html
          doc.write(injected)
        } else {
          doc.write(`<html><head><title>${title}</title>${extraHead}</head><body>${html}</body></html>`)
        }
        doc.close()

        frame.onload = () => {
          const printWindow = frame.contentWindow
          if (!printWindow) {
            cleanup()
            return
          }

          let finished = false
          const done = () => {
            if (finished) return
            finished = true
            cleanup()
          }

          printWindow.addEventListener('afterprint', done, { once: true })

          setTimeout(() => {
            printWindow.focus()
            printWindow.print()
            setTimeout(done, 1500)
          }, delayMs)
        }
      })
    },
    [refreshEventUsers],
  )

  /* ---------- Search ---------- */

  const handleSearch = (val: string) => {
    setSearchValue(val)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      navigate({ search: val, page: 1 })
    }, 400)
  }

  /* ---------- Sorting ---------- */

  const handleSort = (col: string) => {
    const newDir = filters.sort === col && filters.direction === 'asc' ? 'desc' : 'asc'
    navigate({ sort: col, direction: newDir, page: 1 })
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (filters.sort !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
    return filters.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    )
  }

  /* ---------- Selection ---------- */

  const allSelected = eventUsers.length > 0 && eventUsers.every((u) => selected.has(u.id))

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(eventUsers.map((u) => u.id)))
    }
  }

  const toggleOne = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  /* ---------- Badge printing ---------- */

  const badgePrintUrl = (token: string) => `${urls.badgeBase}/${token}/badge?physical=1&mark_printed=1`
  const printPageHead = () => {
    if (!physicalBadgePrint) return '<style>@page{margin:0}body{margin:0;padding:0}</style>'
    const w = physicalBadgeWidth || '3.5'
    const h = physicalBadgeHeight || '5.5'
    return `<style>@page{margin:0;size:${w}in ${h}in}html,body{margin:0;padding:0}</style>`
  }

  const printBadge = async (token: string) => {
    try {
      const res = await fetch(badgePrintUrl(token), { credentials: 'same-origin' })
      if (!res.ok) throw new Error('Failed to fetch badge')
      const html = await res.text()

      await printHtmlInFrame(html, 'Print Badge', 800, printPageHead())
    } catch (err) {
      console.error('Badge print error:', err)
    }
  }

  const printSelectedBadges = async () => {
    if (selected.size === 0) return
    setPrinting(true)

    try {
      let allHtml = ''
      const tokens = eventUsers.filter((u) => selected.has(u.id)).map((u) => u.token)

      for (const token of tokens) {
        const res = await fetch(badgePrintUrl(token), { credentials: 'same-origin' })
        if (res.ok) {
          const html = await res.text()
          allHtml += `<div style="page-break-after:always">${html}</div>`
        }
      }

      if (!allHtml) return

      await printHtmlInFrame(
        allHtml,
        'Print Badges',
        1200,
        printPageHead(),
      )
    } finally {
      setPrinting(false)
    }
  }

  /* ---------- CSV export (server-side, all rows) ---------- */

  const exportCsv = () => {
    const params = new URLSearchParams()
    if (filters.search) params.set('search', filters.search)
    if (filters.sort)   params.set('sort', filters.sort)
    if (filters.direction) params.set('direction', filters.direction)
    if (filters.userType) params.set('user_type', filters.userType)
    const qs = params.toString()
    window.location.href = `${urls.exportCsvBase}${qs ? '?' + qs : ''}`
  }

  /* ---------- Edit modal ---------- */

  const openEdit = (eu: EventUserData) => {
    setEditUser(eu)
    setEditForm({
      title: eu.title || '',
      first_name: eu.firstName || '',
      last_name: eu.lastName || '',
      phone_number: eu.phoneNumber || '',
      email: eu.email || '',
      organization: eu.organization || '',
      position: eu.position || '',
      country: eu.country || '',
      city: eu.city || '',
      gender: eu.gender || '',
      age: eu.age != null ? String(eu.age) : '',
      id_number: eu.idNumber || '',
      passport_number: eu.passportNumber || '',
      blood_type: eu.bloodType || '',
      table_number: eu.tableNumber != null ? String(eu.tableNumber) : '',
      seat_row: eu.seatRow || '',
      seat_column: eu.seatColumn != null ? String(eu.seatColumn) : '',
      user_type: eu.userType || '',
    })
    const cf: Record<string, string> = {}
    customFieldDefs.forEach(def => {
      const cfKey = def.nameEn || def.name
      cf[cfKey] = eu.customFields?.[cfKey] || ''
    })
    setEditCustomForm(cf)
  }

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!editUser) return

    let phoneNumber = editForm.phone_number || ''
    if (hasPhoneField) {
      const phoneCheck = await editPhoneRef.current?.validatePhone({ required: false })
      if (phoneCheck && !phoneCheck.empty && !phoneCheck.valid) {
        setEditPhoneError(t.invalidPhoneNumber || 'Please enter a valid phone number.')
        return
      }
      setEditPhoneError('')
      if (phoneCheck?.number) phoneNumber = phoneCheck.number
    }

    setSaving(true)

    const payload: Record<string, unknown> = { ...editForm, phone_number: phoneNumber }
    if (Object.keys(editCustomForm).length > 0) {
      payload.custom_fields = editCustomForm
    }

    const updateUrl = urls.updateAdminBase.replace(':token', editUser.token)
    router.put(
      updateUrl,
      { event_user: payload },
      {
        preserveScroll: true,
        onFinish: () => setSaving(false),
        onSuccess: () => setEditUser(null),
      },
    )
  }

  /* ---------- Create modal ---------- */

  const openCreate = () => {
    setCreateForm({ event_id: String(event.id) })
    setCreateCustomForm({})
    setCreateOpen(true)
  }

  const handleCreateSubmit = async (e: FormEvent) => {
    e.preventDefault()

    let phoneNumber = createForm.phone_number || ''
    if (hasPhoneField) {
      const phoneCheck = await createPhoneRef.current?.validatePhone({ required: false })
      if (phoneCheck && !phoneCheck.empty && !phoneCheck.valid) {
        setCreatePhoneError(t.invalidPhoneNumber || 'Please enter a valid phone number.')
        return
      }
      setCreatePhoneError('')
      if (phoneCheck?.number) phoneNumber = phoneCheck.number
    }

    setCreating(true)

    const payload: Record<string, unknown> = { ...createForm, phone_number: phoneNumber }
    if (Object.keys(createCustomForm).length > 0) {
      payload.custom_fields = createCustomForm
    }

    router.post(
      urls.createAdmin,
      { event_user: payload },
      {
        preserveScroll: true,
        onFinish: () => setCreating(false),
        onSuccess: () => {
          setCreateOpen(false)
          setCreateCustomForm({})
        },
      },
    )
  }

  /* ---------- PDF export (selected or all) ---------- */

  const exportPdf = () => {
    const ids = selected.size > 0
      ? Array.from(selected)
      : eventUsers.map((u) => u.id)
    if (ids.length === 0) return

    const params = new URLSearchParams()
    ids.forEach((id) => params.append('event_users[ids][]', String(id)))
    window.location.href = `${urls.renderAll.replace('.pdf', '')}.pdf?${params.toString()}`
  }

  /* ---------- Approval status badge ---------- */

  const approvalBadge = (eu: EventUserData) => {
    if (eu.approved === true)
      return (
        <Badge className="bg-[#DBEDDB] text-[#37352F] hover:bg-[#DBEDDB] text-[11px] font-medium px-1.5 py-0">
          <Check className="h-3 w-3 mr-0.5" />
          {t.approved}
        </Badge>
      )
    if (eu.approved === false)
      return (
        <Badge className="bg-[#FBE4E4] text-[#37352F] hover:bg-[#FBE4E4] text-[11px] font-medium px-1.5 py-0">
          <X className="h-3 w-3 mr-0.5" />
          {t.rejected}
        </Badge>
      )
    return (
      <Badge className="bg-[#FDECC8] text-[#37352F] hover:bg-[#FDECC8] text-[11px] font-medium px-1.5 py-0">
        <Clock className="h-3 w-3 mr-0.5" />
        {t.pending}
      </Badge>
    )
  }

  /* ---------- Dynamic columns from registrationFields ---------- */

  const dynColumns = registrationFields.map((f) => ({
    key: f,
    dbCol: f, // for server sort
    label: t[f] || f.replace(/_/g, ' '),
    accessor: FIELD_MAP[f] as keyof EventUserData | undefined,
  }))

  /* ---------- Pagination ---------- */

  const { currentPage, totalPages, totalCount, perPage } = pagination
  const startRow = Math.min((currentPage - 1) * perPage + 1, totalCount)
  const endRow = Math.min(currentPage * perPage, totalCount)

  /* ---------- Render helpers for edit form fields ---------- */

  const renderEditField = (fieldName: string) => {
    const val = editForm[fieldName.toLowerCase().replace(/ /g, '_')] || ''
    const fieldKey = fieldName.toLowerCase().replace(/ /g, '_')

    const common = {
      value: val,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setEditForm((prev) => ({ ...prev, [fieldKey]: e.target.value })),
      className:
        'h-8 rounded-md border border-[#E8E8E5] bg-white px-3 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] focus-visible:outline-none w-full',
    }

    switch (fieldName) {
      case 'User type':
        return (
          <select {...common} value={editForm.user_type || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, user_type: e.target.value }))}>
            <option value="">{t.pleaseSelect}</option>
            {eventUserTypes.map((ut) => (
              <option key={ut} value={ut}>
                {ut}
              </option>
            ))}
          </select>
        )
      case 'Gender':
        return (
          <select {...common} value={editForm.gender || ''} onChange={(e) => setEditField('gender', e.target.value)}>
            <option value="">{t.pleaseSelect}</option>
            <option value={t.male}>{t.male}</option>
            <option value={t.female}>{t.female}</option>
          </select>
        )
      case 'Title':
        return renderTitleField(editForm, setEditField)
      case 'Blood type':
        return (
          <select {...common} value={editForm.blood_type || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, blood_type: e.target.value }))}>
            <option value="">—</option>
            {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>
        )
      case 'Phone number':
        return (
          <>
            <PhoneInput
              ref={editPhoneRef}
              value={editForm.phone_number || ''}
              onChangeNumber={(num) => {
                setEditForm((prev) => ({ ...prev, phone_number: num }))
                if (editPhoneError) setEditPhoneError('')
              }}
              inputProps={{ className: common.className }}
            />
            {editPhoneError && <p className="mt-1 text-xs text-red-500">{editPhoneError}</p>}
          </>
        )
      case 'Age':
        return <Input {...common} type="number" min="1" max="100" value={editForm.age || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, age: e.target.value }))} />
      case 'Seat row':
        return <Input {...common} type="number" value={editForm.seat_row || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, seat_row: e.target.value }))} />
      case 'Seat column':
        return <Input {...common} type="number" value={editForm.seat_column || ''} onChange={(e) => setEditForm((prev) => ({ ...prev, seat_column: e.target.value }))} />
      case 'Position':
        return renderPositionField(editForm, setEditField)
      default:
        return <Input {...common} />
    }
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <AppShell
      locale={locale}
      logoUrl={logoUrl}
      accountName={accountName}
      userEmail={userEmail}
      canEditAccount={canEditAccount}
      wideLayout={wideLayout}
      urls={urls}
      translations={t as any}
    >
      <Head title={`${t.registeredUsers} — ${event.name}`} />

      <div className="flex-1 overflow-auto">
        <div className="py-6">
          {/* ---- Back + Page header ---- */}
          <div className="flex items-center gap-3 mb-1">
            <a
              href={urls.eventDashboard}
              className="flex items-center justify-center h-9 w-9 rounded hover:bg-[#F1F1EF] transition-colors text-[#91918E] hover:text-[#37352F]"
              title={event.name}
            >
              <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
            </a>
            <div className="flex items-center justify-center h-9 w-9 rounded bg-[#D3E5EF] text-lg">
              <Users className="h-[18px] w-[18px] text-[#37352F]" />
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-[#37352F] leading-tight">
                {t.registeredUsers}
              </h1>
              <p className="text-[13px] text-[#91918E]">
                {event.name} · {totalCount} {t.users}
              </p>
            </div>
          </div>

          {/* ---- Toolbar ---- */}
          <div className="flex items-center justify-between mt-4 mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              {searchVisible ? (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#91918E]" />
                  <Input
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder={t.search}
                    className="h-8 w-56 pl-8 text-sm border-[#E8E8E5] focus:border-[#2383E2]"
                    autoFocus
                    onBlur={() => {
                      if (!searchValue) setSearchVisible(false)
                    }}
                  />
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                  onClick={() => setSearchVisible(true)}
                >
                  <Search className="h-3.5 w-3.5 mr-1.5" />
                  {t.search}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Export CSV */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                onClick={exportCsv}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {t.exportCsv}
              </Button>

              {/* Print selected */}
              {selected.size > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                  onClick={printSelectedBadges}
                  disabled={printing}
                >
                  {printing ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Printer className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {t.printSelectedBadges} ({selected.size})
                </Button>
              )}

              {/* Get selected as PDF */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                onClick={exportPdf}
              >
                <FileDown className="h-3.5 w-3.5 mr-1.5" />
                {selected.size > 0
                  ? `${t.getAllAsPdf} (${selected.size})`
                  : t.getAllAsPdf}
              </Button>

              {/* Import */}
              {permissions.canCreate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                  onClick={() => (window.location.href = urls.importNew)}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {t.importUsers}
                </Button>
              )}

              {/* Bulk Create */}
              {permissions.canCreate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[#91918E] hover:text-[#37352F] hover:bg-[#F1F1EF]"
                  onClick={() => (window.location.href = urls.bulkCreateNew)}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  {t.bulkCreateUsers}
                </Button>
              )}

              {/* New Event User */}
              {permissions.canCreate && (
                <Button
                  size="sm"
                  className="h-8 bg-[#2383E2] hover:bg-[#1b6ec2] text-white"
                  onClick={openCreate}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  {t.newEventUser}
                </Button>
              )}
            </div>
          </div>

          {/* ---- Column filters ---- */}
          {(eventUserTypes.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mb-3 px-1">
              <span className="text-[11px] font-medium text-[#91918E]">{t.search ? 'Filter:' : 'Filter:'}</span>

              {eventUserTypes.length > 0 && (
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-[#91918E] whitespace-nowrap">{t.userType}:</span>
                  <select
                    value={filters.userType}
                    onChange={(e) => navigate({ user_type: e.target.value, page: 1 })}
                    className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[12px] text-[#37352F] focus:border-[#2383E2] focus:outline-none"
                  >
                    <option value="">All</option>
                    {eventUserTypes.map((ut) => <option key={ut} value={ut}>{ut}</option>)}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-1">
                <span className="text-[11px] text-[#91918E] whitespace-nowrap">{t.printed}:</span>
                <select
                  value={filters.printed}
                  onChange={(e) => navigate({ printed: e.target.value, page: 1 })}
                  className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[12px] text-[#37352F] focus:border-[#2383E2] focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="true">{t.true_}</option>
                  <option value="false">{t.false_}</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <span className="text-[11px] text-[#91918E] whitespace-nowrap">{t.checkIn}:</span>
                <select
                  value={filters.checkedIn}
                  onChange={(e) => navigate({ checked_in: e.target.value, page: 1 })}
                  className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[12px] text-[#37352F] focus:border-[#2383E2] focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="true">{t.checkedIn}</option>
                  <option value="false">{t.false_}</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <span className="text-[11px] text-[#91918E] whitespace-nowrap">{t.rsvp}:</span>
                <select
                  value={filters.rsvp}
                  onChange={(e) => navigate({ rsvp: e.target.value, page: 1 })}
                  className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[12px] text-[#37352F] focus:border-[#2383E2] focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="accepted">accepted</option>
                  <option value="declined">declined</option>
                </select>
              </div>

              {(filters.userType || filters.printed || filters.checkedIn || filters.rsvp) && (
                <button
                  onClick={() => navigate({ user_type: '', printed: '', checked_in: '', rsvp: '', page: 1 })}
                  className="text-[11px] text-[#EB5757] hover:underline"
                >
                  ✕ Clear filters
                </button>
              )}
            </div>
          )}

          {/* ---- Table ---- */}
          <div className="border border-[#E8E8E5] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F7F7F5] hover:bg-[#F7F7F5]">
                    {/* Checkbox */}
                    <TableHead className="w-10 text-center">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                      />
                    </TableHead>

                    {/* ID */}
                    <TableHead className="cursor-pointer select-none text-[#91918E] text-[12px] font-medium whitespace-nowrap" onClick={() => handleSort('id')}>
                      <span className="flex items-center">
                        {t.id}
                        <SortIcon col="id" />
                      </span>
                    </TableHead>

                    {/* Register Date */}
                    <TableHead className="cursor-pointer select-none text-[#91918E] text-[12px] font-medium whitespace-nowrap" onClick={() => handleSort('created_at')}>
                      <span className="flex items-center">
                        {t.registerDate}
                        <SortIcon col="created_at" />
                      </span>
                    </TableHead>

                    {/* Dynamic registration columns */}
                    {dynColumns.map((c) => (
                      <TableHead
                        key={c.key}
                        className="cursor-pointer select-none text-[#91918E] text-[12px] font-medium whitespace-nowrap"
                        onClick={() => handleSort(c.dbCol)}
                      >
                        <span className="flex items-center">
                          {c.label}
                          <SortIcon col={c.dbCol} />
                        </span>
                      </TableHead>
                    ))}

                    {/* User Type */}
                    <TableHead className="cursor-pointer select-none text-[#91918E] text-[12px] font-medium whitespace-nowrap" onClick={() => handleSort('user_type')}>
                      <span className="flex items-center">
                        {t.userType}
                        <SortIcon col="user_type" />
                      </span>
                    </TableHead>

                    {/* Token */}
                    <TableHead className="text-[#91918E] text-[12px] font-medium whitespace-nowrap">
                      {t.token}
                    </TableHead>

                    {/* Printed */}
                    <TableHead className="text-[#91918E] text-[12px] font-medium whitespace-nowrap">
                      {t.printed}
                    </TableHead>

                    {/* Checked In */}
                    <TableHead className="text-[#91918E] text-[12px] font-medium whitespace-nowrap">
                      {t.checkIn}
                    </TableHead>

                    {/* RSVP */}
                    <TableHead className="text-[#91918E] text-[12px] font-medium whitespace-nowrap">
                      {t.rsvp}
                    </TableHead>

                    {/* Notes */}
                    <TableHead className="text-[#91918E] text-[12px] font-medium whitespace-nowrap">
                      {t.notes}
                    </TableHead>

                    {/* Print badge */}
                    <TableHead className="w-10" />

                    {/* Actions */}
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {eventUsers.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={7 + dynColumns.length + 5}
                        className="text-center py-16 text-[#91918E]"
                      >
                        <Users className="h-10 w-10 mx-auto mb-3 text-[#D3D3D0]" />
                        <p className="text-sm">{t.noUsers}</p>
                      </TableCell>
                    </TableRow>
                  )}

                  {eventUsers.map((eu) => (
                    <TableRow
                      key={eu.id}
                      className="group hover:bg-[#F7F7F5] transition-colors"
                    >
                      {/* Checkbox */}
                      <TableCell className="text-center">
                        <Checkbox
                          checked={selected.has(eu.id)}
                          onCheckedChange={() => toggleOne(eu.id)}
                        />
                      </TableCell>

                      {/* ID */}
                      <TableCell className="text-sm text-[#37352F] font-medium tabular-nums">
                        {eu.id}
                      </TableCell>

                      {/* Date */}
                      <TableCell className="text-sm text-[#37352F] tabular-nums whitespace-nowrap">
                        {eu.createdAt}
                      </TableCell>

                      {/* Dynamic fields */}
                      {dynColumns.map((c) => (
                        <TableCell key={c.key} className="text-sm text-[#37352F] max-w-[200px] truncate">
                          {c.accessor ? String(eu[c.accessor] ?? '') : ''}
                        </TableCell>
                      ))}

                      {/* User Type */}
                      <TableCell className="text-sm text-[#37352F]">
                        <div className="space-y-1">
                          <div>{eu.userType || '—'}</div>
                          <div className="flex flex-wrap gap-1">
                            <Badge className={`text-[11px] font-medium px-1.5 py-0 ${eu.primaryRegistration ? 'bg-[#DBEDDB] text-[#37352F] hover:bg-[#DBEDDB]' : 'bg-[#F1F1EF] text-[#37352F] hover:bg-[#F1F1EF]'}`}>
                              {eu.primaryRegistration ? t.primaryRegistration : (t.groupedTicket || 'Grouped Ticket')}
                            </Badge>
                            <Badge className="bg-[#E8F0FE] text-[#37352F] hover:bg-[#E8F0FE] text-[11px] font-medium px-1.5 py-0">
                              {t.bundleSize}: {eu.bundleSize}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>

                      {/* Token */}
                      <TableCell className="text-xs text-[#91918E] font-mono max-w-[160px]">
                        <div className="truncate">{eu.token}</div>
                        {eu.registrationGroupToken && (
                          <div className="truncate text-[10px] text-[#B0B0AA] mt-1">
                            {eu.registrationGroupToken}
                          </div>
                        )}
                      </TableCell>

                      {/* Printed */}
                      <TableCell className="text-sm">
                        {eu.printed ? (
                          <span className="text-green-600">{t.true_}</span>
                        ) : (
                          <span className="text-[#91918E]">{t.false_}</span>
                        )}
                      </TableCell>

                      {/* Checked In */}
                      <TableCell className="text-sm">
                        {eu.checkedIn ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <Check className="h-3.5 w-3.5" />
                            {t.checkedIn}
                          </span>
                        ) : (
                          <span className="text-[#91918E]">{t.false_}</span>
                        )}
                      </TableCell>

                      {/* RSVP */}
                      <TableCell className="text-sm">
                        {eu.rsvp ? (
                          <Badge
                            className={`text-[11px] font-medium px-1.5 py-0 ${eu.rsvp === 'accepted' ? 'bg-[#DBEDDB] text-[#37352F] hover:bg-[#DBEDDB]' : 'bg-[#FBE4E4] text-[#37352F] hover:bg-[#FBE4E4]'}`}
                          >
                            {eu.rsvp}
                          </Badge>
                        ) : (
                          <span className="text-[#D3D3D0]">—</span>
                        )}
                      </TableCell>

                      {/* Notes */}
                      <TableCell className="text-sm">
                        {eu.notesCount > 0 ? (
                          <button
                            type="button"
                            onClick={() =>
                              router.visit(urls.detailsBase.replace(':id', String(eu.id)))
                            }
                            className="inline-flex items-center gap-1 text-[#2383E2] hover:underline"
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            {eu.notesCount}
                          </button>
                        ) : (
                          <span className="text-[#D3D3D0]">0</span>
                        )}
                      </TableCell>

                      {/* Print badge button */}
                      <TableCell>
                        <TooltipProvider delay={300}>
                          <Tooltip>
                            <TooltipTrigger
                              onClick={() => printBadge(eu.token)}
                              className="flex items-center justify-center h-7 w-7 rounded-[4px] hover:bg-[#EDEDEC] text-[#91918E] hover:text-[#37352F] cursor-pointer outline-none transition-colors"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">{t.badgeLink}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>

                      {/* Actions dropdown */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="flex items-center justify-center h-7 w-7 rounded-[4px] p-0 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[#EDEDEC] text-[#91918E] cursor-pointer outline-none transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onClick={() =>
                                router.visit(urls.detailsBase.replace(':id', String(eu.id)))
                              }
                            >
                              <Eye className="h-3.5 w-3.5 mr-2" />
                              {t.viewDetails}
                            </DropdownMenuItem>
                            {permissions.canEdit && (
                              <DropdownMenuItem onClick={() => openEdit(eu)}>
                                <Pencil className="h-3.5 w-3.5 mr-2" />
                                {t.edit}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => {
                                const rsvpUrl = urls.rsvpBase.replace(':token', eu.token)
                                window.open(rsvpUrl, '_blank')
                              }}
                            >
                              <ExternalLink className="h-3.5 w-3.5 mr-2" />
                              {t.rsvpLink}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                const resendUrl = urls.resendRegistrationBase.replace(':token', eu.token)
                                router.post(resendUrl, {}, { preserveScroll: true })
                              }}
                            >
                              <Download className="h-3.5 w-3.5 mr-2" />
                              {t.resendRegistration}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                router.post(
                                  urls.toggleCheckIn,
                                  { token: eu.token },
                                  { preserveScroll: true, only: ['eventUsers'] }
                                )
                              }}
                            >
                              <Check className={`h-3.5 w-3.5 mr-2 ${eu.checkedIn ? 'text-orange-500' : 'text-green-600'}`} />
                              {eu.checkedIn ? t.undoCheckIn : t.checkIn}
                            </DropdownMenuItem>
                            {eu.approved !== true && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() =>
                                    router.get(urls.approve, { id: eu.token }, { preserveScroll: true })
                                  }
                                >
                                  <Check className="h-3.5 w-3.5 mr-2 text-green-600" />
                                  {t.approve}
                                </DropdownMenuItem>
                              </>
                            )}
                            {permissions.canDestroy && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-[#EB5757] focus:text-[#EB5757]"
                                  onClick={() => {
                                    if (!window.confirm(`${t.deleteEventUser || 'Delete this user?'} (ID: ${eu.id})`)) return
                                    const url = urls.deleteBase.replace(':id', eu.token)
                                    router.delete(url, {
                                      preserveScroll: true,
                                      onSuccess: () => navigate({ page: pagination.currentPage }),
                                    })
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                                  {t.deleteEventUser || 'Delete'}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* ---- Pagination footer ---- */}
            {totalCount > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E8E5] bg-white">
                <div className="flex items-center gap-2 text-[13px] text-[#91918E]">
                  {t.showing} {startRow}–{endRow} {t.of} {totalCount} {t.users}
                  <span className="mx-2 text-[#E8E8E5]">|</span>
                  <span>{t.rowsPerPage}</span>
                  <select
                    value={perPage}
                    onChange={(e) => navigate({ per_page: Number(e.target.value), page: 1 })}
                    className="h-7 rounded border border-[#E8E8E5] bg-white px-1.5 text-[13px] text-[#37352F]"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage <= 1}
                    onClick={() => navigate({ page: 1 })}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage <= 1}
                    onClick={() => navigate({ page: currentPage - 1 })}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <span className="text-[13px] text-[#37352F] px-2 tabular-nums">
                    {currentPage} / {totalPages || 1}
                  </span>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage >= totalPages}
                    onClick={() => navigate({ page: currentPage + 1 })}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage >= totalPages}
                    onClick={() => navigate({ page: totalPages })}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- Create Modal ---- */}
      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#37352F]">{t.newEventUser}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit} className="space-y-4 py-2">
            <input type="hidden" name="event_user[event_id]" value={event.id} />

            {/* User type — only when event has configured types */}
            {hasUserTypes && (
            <div>
              <Label className="text-[13px] text-[#37352F] mb-1 block">
                {t.userType || 'User Type'}
              </Label>
              <select
                className="h-8 rounded-md border border-[#E8E8E5] bg-white px-3 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] focus-visible:outline-none w-full"
                value={createForm.user_type || ''}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, user_type: e.target.value }))}
              >
                <option value="">{t.pleaseSelect}</option>
                {eventUserTypes.map((ut) => (
                  <option key={ut} value={ut}>{ut}</option>
                ))}
              </select>
            </div>
            )}

            {/* Standard registration fields (skip user_type – already shown above) */}
            {formFields.filter((ff) => ff.field && ff.field !== 'User type' && !ff.custom).map((ff) => {
              const fieldKey = ff.field.toLowerCase().replace(/ /g, '_')
              const labelKey = fieldKey as string
              const val = createForm[fieldKey] || ''

              const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
                setCreateForm((prev) => ({ ...prev, [fieldKey]: e.target.value }))

              const inputCls = INPUT_CLS
              let field: React.ReactNode
              switch (ff.field) {
                case 'Title':
                  field = renderTitleField(createForm, setCreateField)
                  break
                case 'Gender':
                  field = (
                    <select className={inputCls} value={createForm.gender || ''} onChange={(e) => setCreateField('gender', e.target.value)}>
                      <option value="">{t.pleaseSelect}</option>
                      <option value={t.male}>{t.male}</option>
                      <option value={t.female}>{t.female}</option>
                    </select>
                  )
                  break
                case 'Blood type':
                  field = (
                    <select className={inputCls} value={createForm.blood_type || ''} onChange={(e) => setCreateForm((prev) => ({ ...prev, blood_type: e.target.value }))}>
                      <option value="">—</option>
                      {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((bt) => (
                        <option key={bt} value={bt}>{bt}</option>
                      ))}
                    </select>
                  )
                  break
                case 'Phone number':
                  field = (
                    <>
                      <PhoneInput
                        ref={createPhoneRef}
                        value={val}
                        onChangeNumber={(num) => {
                          setCreateForm((prev) => ({ ...prev, phone_number: num }))
                          if (createPhoneError) setCreatePhoneError('')
                        }}
                        inputProps={{ className: inputCls }}
                      />
                      {createPhoneError && <p className="mt-1 text-xs text-red-500">{createPhoneError}</p>}
                    </>
                  )
                  break
                case 'Age':
                  field = <Input className={inputCls} type="number" min="1" max="100" value={val} onChange={handleChange} />
                  break
                case 'Seat row':
                  field = <Input className={inputCls} type="number" value={val} onChange={handleChange} />
                  break
                case 'Seat column':
                  field = <Input className={inputCls} type="number" value={val} onChange={handleChange} />
                  break
                case 'Position':
                  field = renderPositionField(createForm, setCreateField)
                  break
                default:
                  field = <Input className={inputCls} value={val} onChange={handleChange} />
              }

              return (
                <div key={ff.field}>
                  <Label className="text-[13px] text-[#37352F] mb-1 block">
                    {t[labelKey] || ff.field}
                  </Label>
                  {field}
                </div>
              )
            })}

            {/* Custom fields */}
            {customFieldDefs.map((cf) => {
              const cfKey = cf.nameEn || cf.name
              const val = createCustomForm[cfKey] || ''
              const setVal = (v: string) => setCreateCustomForm((prev) => ({ ...prev, [cfKey]: v }))

              let field: React.ReactNode
              switch (cf.fieldType) {
                case 'drop_down':
                  field = (
                    <select className={INPUT_CLS} value={val} onChange={(e) => setVal(e.target.value)} required={cf.required}>
                      <option value="">{t.pleaseSelect}</option>
                      {cf.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )
                  break
                case 'radio_button':
                  field = (
                    <div className="flex flex-col gap-2">
                      {cf.options.map((o, optionIdx) => {
                        const optionId = `create-cf-${cfKey}-${o.replace(/\s+/g, '-').toLowerCase()}`
                        const iconUrl = cf.optionIconUrls?.[optionIdx]
                        return (
                          <label key={`${optionIdx}-${o}`} htmlFor={optionId} className="flex items-center gap-2 text-[13px] text-[#37352F]">
                            <input
                              type="radio"
                              id={optionId}
                              name={`create-cf-${cfKey}`}
                              value={o}
                              checked={val === o}
                              onChange={() => setVal(o)}
                              required={cf.required && !val}
                              className="h-3.5 w-3.5 border-[#E8E8E5] text-[#2383E2]"
                            />
                            {iconUrl && <img src={iconUrl} alt="" className="h-6 w-6 object-contain" />}
                            <span>{o}</span>
                          </label>
                        )
                      })}
                    </div>
                  )
                  break
                case 'check_box':
                  field = (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={val === '1' || val === 'true'}
                        onChange={(e) => setVal(e.target.checked ? '1' : '0')}
                        className="h-3.5 w-3.5 rounded border-[#E8E8E5]"
                      />
                      <span className="text-[13px] text-[#37352F]">{cf.name}</span>
                    </div>
                  )
                  break
                case 'text_area':
                  field = (
                    <textarea
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      required={cf.required}
                      rows={3}
                      className="flex w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] focus-visible:outline-none resize-y"
                    />
                  )
                  break
                default:
                  field = <Input className={INPUT_CLS} value={val} onChange={(e) => setVal(e.target.value)} required={cf.required} />
              }

              return (
                <div key={cf.name}>
                  {cf.fieldType !== 'check_box' && (
                    <Label className="text-[13px] text-[#37352F] mb-1 block">{cf.name}</Label>
                  )}
                  {field}
                </div>
              )
            })}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                className="text-[#91918E]"
                onClick={() => setCreateOpen(false)}
              >
                {t.cancel}
              </Button>
              <Button
                type="submit"
                className="bg-[#2383E2] hover:bg-[#1b6ec2] text-white"
                disabled={creating}
              >
                {creating && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t.save}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Edit Modal ---- */}
      <Dialog open={editUser !== null} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#37352F]">{t.editEventUser}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4 py-2">
            {/* User type — only when event has configured types */}
            {hasUserTypes && (
            <div>
              <Label className="text-[13px] text-[#37352F] mb-1 block">
                {t.userType || 'User Type'}
              </Label>
              <select
                className="h-8 rounded-md border border-[#E8E8E5] bg-white px-3 text-sm text-[#37352F] focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] focus-visible:outline-none w-full"
                value={editForm.user_type || ''}
                onChange={(e) => setEditForm((prev) => ({ ...prev, user_type: e.target.value }))}
              >
                <option value="">{t.pleaseSelect}</option>
                {eventUserTypes.map((ut) => (
                  <option key={ut} value={ut}>{ut}</option>
                ))}
              </select>
            </div>
            )}

            {/* Standard registration fields (skip user_type – already shown above) */}
            {formFields.filter((ff) => ff.field && ff.field !== 'User type' && !ff.custom).map((ff) => {
              const fieldKey = ff.field.toLowerCase().replace(/ /g, '_')
              const labelKey = fieldKey as string
              return (
                <div key={ff.field}>
                  <Label className="text-[13px] text-[#37352F] mb-1 block">
                    {t[labelKey] || ff.field}
                  </Label>
                  {renderEditField(ff.field)}
                </div>
              )
            })}

            {/* Custom fields */}
            {customFieldDefs.map((cf) => {
              const cfKey = cf.nameEn || cf.name
              const val = editCustomForm[cfKey] || ''
              const setVal = (v: string) => setEditCustomForm((prev) => ({ ...prev, [cfKey]: v }))

              let field: React.ReactNode
              switch (cf.fieldType) {
                case 'drop_down':
                  field = (
                    <select className={INPUT_CLS} value={val} onChange={(e) => setVal(e.target.value)} required={cf.required}>
                      <option value="">{t.pleaseSelect}</option>
                      {cf.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )
                  break
                case 'radio_button':
                  field = (
                    <div className="flex flex-col gap-2">
                      {cf.options.map((o, optionIdx) => {
                        const optionId = `edit-cf-${cfKey}-${o.replace(/\s+/g, '-').toLowerCase()}`
                        const iconUrl = cf.optionIconUrls?.[optionIdx]
                        return (
                          <label key={`${optionIdx}-${o}`} htmlFor={optionId} className="flex items-center gap-2 text-[13px] text-[#37352F]">
                            <input
                              type="radio"
                              id={optionId}
                              name={`edit-cf-${cfKey}`}
                              value={o}
                              checked={val === o}
                              onChange={() => setVal(o)}
                              required={cf.required && !val}
                              className="h-3.5 w-3.5 border-[#E8E8E5] text-[#2383E2]"
                            />
                            {iconUrl && <img src={iconUrl} alt="" className="h-6 w-6 object-contain" />}
                            <span>{o}</span>
                          </label>
                        )
                      })}
                    </div>
                  )
                  break
                case 'check_box':
                  field = (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`edit-cf-${cfKey}`}
                        checked={val === '1' || val === 'true'}
                        onChange={(e) => setVal(e.target.checked ? '1' : '0')}
                        className="h-4 w-4 rounded border-[#E8E8E5]"
                      />
                      <Label htmlFor={`edit-cf-${cfKey}`} className="text-[13px] text-[#37352F]">{cf.name}</Label>
                    </div>
                  )
                  break
                case 'text_area':
                  field = (
                    <textarea
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      required={cf.required}
                      rows={3}
                      className="flex w-full rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2]"
                    />
                  )
                  break
                default:
                  field = (
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      required={cf.required}
                      placeholder={cf.name}
                      className={INPUT_CLS}
                    />
                  )
              }

              if (cf.fieldType === 'check_box') {
                return <div key={cfKey}>{field}</div>
              }
              return (
                <div key={cfKey}>
                  <Label className="text-[13px] text-[#37352F] mb-1 block">{cf.name}</Label>
                  {field}
                </div>
              )
            })}

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                className="text-[#91918E]"
                onClick={() => setEditUser(null)}
              >
                {t.cancel}
              </Button>
              <Button
                type="submit"
                className="bg-[#2383E2] hover:bg-[#1b6ec2] text-white"
                disabled={saving}
              >
                {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t.save}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  )
}
